import { useState, useRef } from "react";
import { ModalLayout } from "./ModalLayout";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { colors, typography, effects, components } from "../../design/tokens";
import { designLibrary } from "../../data/designLibrary";

interface DesignLibraryModalProps {
  onClose: () => void;
  onSelectDesign: (design: { id: string; name: string; description: string; promptText: string }) => void;
}

export function DesignLibraryModal({
  onClose,
  onSelectDesign,
}: DesignLibraryModalProps) {
  const isCompactView = useIsCompactView();
  const [currentIndex, setCurrentIndex] = useState(0);

  const currentDesign = designLibrary[currentIndex];

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < designLibrary.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  // Swipe handling for mobile
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }

    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleUseDesign = () => {
    onSelectDesign({
      id: currentDesign.id,
      name: currentDesign.name,
      description: currentDesign.description,
      promptText: currentDesign.promptText,
    });
    onClose();
  };

  // Compact Layout for Mobile - Full-screen carousel
  if (isCompactView) {
    return (
      <div
        className={`fixed inset-0 z-50 flex flex-col
          ${components.modal.overlay}
          transition-opacity duration-300`}
      >
        <div
          className="w-full h-full flex flex-col overflow-hidden"
          style={{ backgroundColor: colors.hex.surfaceSecondary }}
        >
          {/* Header with Back Button and Fraction Counter */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-dark-border">
            <div className="flex items-center">
              <button
                onClick={onClose}
                className={`${components.modal.closeButton} mr-4`}
                aria-label="Close"
              >
                <LuChevronLeft size={24} />
              </button>
              <h2 className={`${typography.size["2xl"]} ${typography.weight.bold} ${colors.text.primary}`}>
                Design Library
              </h2>
            </div>
            {/* Fraction Counter */}
            <span className={`${typography.size.sm} ${colors.text.secondary}`}>
              {currentIndex + 1} / {designLibrary.length}
            </span>
          </div>

          {/* Horizontal Swipe Carousel */}
          <div
            className="flex-1 overflow-hidden relative"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex transition-transform duration-300 ease-out h-full"
              style={{
                transform: `translateX(calc(-${currentIndex * 85}% + ${currentIndex > 0 ? '7.5%' : '0px'}))`
              }}
            >
              {designLibrary.map((design, index) => (
                <div
                  key={design.id}
                  className={`flex-shrink-0 w-[85%] px-4 py-4 overflow-y-auto
                    ${index === currentIndex ? 'opacity-100' : 'opacity-40'}
                    transition-opacity duration-300`}
                >
                  {/* Screenshots - 3 in one row */}
                  <div className="flex gap-2 mb-4">
                    {design.screenshots.map((screenshot, i) => (
                      <img
                        key={i}
                        src={`/design-library/${screenshot}`}
                        alt={`${design.name} - Screen ${i + 1}`}
                        className={`flex-1 w-0 object-contain ${effects.borderRadius.lg}`}
                      />
                    ))}
                  </div>

                  {/* Design Name */}
                  <h3 className={`${typography.size["2xl"]} ${typography.weight.bold} ${colors.text.primary} mb-3`}>
                    {design.name}
                  </h3>

                  {/* Description */}
                  <p className={`${colors.text.secondary} leading-relaxed mb-4`}>
                    {design.description}
                  </p>

                  {/* Best For Tags */}
                  <div>
                    <p className={`${typography.size.sm} ${colors.text.secondary} mb-2`}>Best For:</p>
                    <div className="flex flex-wrap gap-2">
                      {design.bestFor.map((tag) => (
                        <span
                          key={tag}
                          className={`inline-block px-3 py-1
                            bg-dark-surface-primary border border-dark-border
                            ${colors.text.secondary} ${effects.borderRadius.full}
                            ${typography.size.sm}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fixed Bottom Section */}
          <div className="px-4 py-4 border-t border-dark-border">
            {/* Use This Design Button */}
            <button
              onClick={handleUseDesign}
              className={`${components.button.primaryLarge} w-full`}
            >
              Use This Design
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop Layout - Two-column
  return (
    <ModalLayout onClose={onClose} className="!w-[1000px] max-w-[95vw]">
      <div className="flex flex-col h-[600px] w-full">
        {/* Header */}
        <div className={`p-6 border-b border-dark-border`}>
          <h2 className={`${typography.size["2xl"]} ${typography.weight.bold} ${colors.text.primary}`}>
            Design Library
          </h2>
        </div>

        {/* Two-column content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Three Screenshots Side by Side */}
          <div className="flex-1 p-4 flex items-center justify-center gap-2 border-r border-dark-border overflow-hidden">
            {currentDesign.screenshots.map((screenshot, index) => (
              <img
                key={index}
                src={`/design-library/${screenshot}`}
                alt={`${currentDesign.name} - Screen ${index + 1}`}
                className={`max-h-full w-auto object-contain ${effects.borderRadius.lg}`}
              />
            ))}
          </div>

          {/* Right Panel - Details */}
          <div className="w-[360px] shrink-0 flex flex-col">
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 pb-0">
              {/* Design Name */}
              <h3 className={`${typography.size["2xl"]} ${typography.weight.bold} ${colors.text.primary} mb-4`}>
                {currentDesign.name}
              </h3>

              {/* Description */}
              <p className={`${colors.text.secondary} leading-relaxed mb-6`}>
                {currentDesign.description}
              </p>

              {/* Best For Tags */}
              <div>
                <p className={`${typography.size.sm} ${colors.text.secondary} mb-2`}>Best For:</p>
                <div className="flex flex-wrap gap-2">
                  {currentDesign.bestFor.map((tag) => (
                    <span
                      key={tag}
                      className={`inline-block px-3 py-1
                        bg-dark-surface-primary border border-dark-border
                        ${colors.text.secondary} ${effects.borderRadius.full}
                        ${typography.size.sm}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Fixed Button */}
            <div className="p-6 pt-4">
              <button
                onClick={handleUseDesign}
                className={`${components.button.primaryLarge} w-full`}
              >
                Use This Design
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Navigation */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-dark-border">
          {/* Dots */}
          <div className="flex gap-1.5">
            {designLibrary.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === currentIndex ? "bg-blue-500" : "bg-dark-border"
                }`}
                aria-label={`Go to design ${i + 1}`}
              />
            ))}
          </div>

          {/* Arrows */}
          <div className="flex gap-2">
            <button
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              className={`p-2 ${effects.borderRadius.lg} border border-dark-border
                ${currentIndex === 0 ? "opacity-30 cursor-not-allowed" : colors.hover.opacity}
                ${colors.text.secondary}`}
              aria-label="Previous design"
            >
              <LuChevronLeft size={20} />
            </button>
            <button
              onClick={goToNext}
              disabled={currentIndex === designLibrary.length - 1}
              className={`p-2 ${effects.borderRadius.lg} border border-dark-border
                ${currentIndex === designLibrary.length - 1 ? "opacity-30 cursor-not-allowed" : colors.hover.opacity}
                ${colors.text.secondary}`}
              aria-label="Next design"
            >
              <LuChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
}
